/**
 * Zustand store для управления семьями, персонами и историями
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { familiesAPI, personsAPI, storiesAPI } from '../api/client';

export const useFamilyStore = create(
  persist(
    (set, get) => ({
  // ==================== State ====================
  families: [],
  currentFamily: null,
  currentFamilyId: null,

  persons: [],
  currentPerson: null,

  treeData: null, // { nodes: [], connections: [] }

  stories: [],
  currentStory: null,

  members: [],
  invitations: [],

  isLoading: false,
  error: null,

  // ==================== Families ====================

  fetchFamilies: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await familiesAPI.list();
      set({ families: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось загрузить семьи', isLoading: false });
      throw error;
    }
  },

  createFamily: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await familiesAPI.create(data);
      const newFamily = response.data;
      set(state => ({
        families: [...state.families, newFamily],
        currentFamily: newFamily,
        currentFamilyId: newFamily.id,
        isLoading: false,
      }));
      return newFamily;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось создать семью', isLoading: false });
      throw error;
    }
  },

  setCurrentFamilyId: (familyId) => {
    set((state) => ({
      currentFamilyId: familyId,
      currentFamily:
        familyId == null
          ? null
          : state.currentFamily?.id === familyId
          ? state.currentFamily
          : state.families.find((family) => family.id === familyId) || null,
    }));
  },

  selectFamily: async (familyId) => {
    set({ isLoading: true, error: null, currentFamilyId: familyId });
    try {
      const response = await familiesAPI.get(familyId);
      set({ currentFamily: response.data, isLoading: false });
      // Also load tree data
      await get().fetchTreeData(familyId);
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось загрузить семью', isLoading: false });
      throw error;
    }
  },

  updateFamily: async (familyId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await familiesAPI.update(familyId, data);
      const updated = response.data;
      set(state => ({
        families: state.families.map(f => f.id === familyId ? updated : f),
        currentFamily: state.currentFamilyId === familyId ? updated : state.currentFamily,
        isLoading: false,
      }));
      return updated;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось обновить семью', isLoading: false });
      throw error;
    }
  },

  deleteFamily: async (familyId) => {
    set({ isLoading: true, error: null });
    try {
      await familiesAPI.delete(familyId);
      set(state => ({
        families: state.families.filter(f => f.id !== familyId),
        currentFamily: state.currentFamilyId === familyId ? null : state.currentFamily,
        currentFamilyId: state.currentFamilyId === familyId ? null : state.currentFamilyId,
        isLoading: false,
      }));
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось удалить семью', isLoading: false });
      throw error;
    }
  },

  // ==================== Tree Data ====================

  fetchTreeData: async (familyId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await personsAPI.getTree(familyId);
      set({ treeData: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось загрузить древо', isLoading: false });
      throw error;
    }
  },

  // ==================== Persons ====================

  fetchPersons: async (familyId, params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await personsAPI.list(familyId, params);
      set({ persons: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось загрузить персон', isLoading: false });
      throw error;
    }
  },

  createPerson: async (familyId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await personsAPI.create(familyId, data);
      const newPerson = response.data;
      set(state => ({
        persons: [...state.persons, newPerson],
        isLoading: false,
      }));
      // Refresh tree data
      await get().fetchTreeData(familyId);
      return newPerson;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось создать персону', isLoading: false });
      throw error;
    }
  },

  selectPerson: async (personId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await personsAPI.get(personId);
      set({ currentPerson: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось загрузить персону', isLoading: false });
      throw error;
    }
  },

  updatePerson: async (personId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await personsAPI.update(personId, data);
      const updated = response.data;
      set(state => ({
        persons: state.persons.map(p => p.id === personId ? updated : p),
        currentPerson: state.currentPerson?.id === personId ? updated : state.currentPerson,
        isLoading: false,
      }));
      // Refresh tree data if we have a current family
      if (get().currentFamilyId) {
        await get().fetchTreeData(get().currentFamilyId);
      }
      return updated;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось обновить персону', isLoading: false });
      throw error;
    }
  },

  deletePerson: async (personId) => {
    set({ isLoading: true, error: null });
    try {
      await personsAPI.delete(personId);
      set(state => ({
        persons: state.persons.filter(p => p.id !== personId),
        currentPerson: state.currentPerson?.id === personId ? null : state.currentPerson,
        isLoading: false,
      }));
      // Refresh tree data
      if (get().currentFamilyId) {
        await get().fetchTreeData(get().currentFamilyId);
      }
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось удалить персону', isLoading: false });
      throw error;
    }
  },

  // ==================== Relations ====================

  createRelation: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await personsAPI.createRelation(data);
      // Refresh tree data
      if (get().currentFamilyId) {
        await get().fetchTreeData(get().currentFamilyId);
      }
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось создать связь', isLoading: false });
      throw error;
    }
  },

  deleteRelation: async (relationId) => {
    set({ isLoading: true, error: null });
    try {
      await personsAPI.deleteRelation(relationId);
      // Refresh tree data
      if (get().currentFamilyId) {
        await get().fetchTreeData(get().currentFamilyId);
      }
      set({ isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось удалить связь', isLoading: false });
      throw error;
    }
  },

  // ==================== Stories ====================

  fetchStories: async (familyId, params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await storiesAPI.listByFamily(familyId, params);
      set({ stories: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось загрузить истории', isLoading: false });
      throw error;
    }
  },

  fetchPersonStories: async (personId, params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await storiesAPI.listByPerson(personId, params);
      set({ stories: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось загрузить истории', isLoading: false });
      throw error;
    }
  },

  createStory: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await storiesAPI.create(data);
      const newStory = response.data;
      set(state => ({
        stories: [...state.stories, newStory],
        isLoading: false,
      }));
      return newStory;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось создать историю', isLoading: false });
      throw error;
    }
  },

  selectStory: async (storyId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await storiesAPI.get(storyId);
      set({ currentStory: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось загрузить историю', isLoading: false });
      throw error;
    }
  },

  updateStory: async (storyId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await storiesAPI.update(storyId, data);
      const updated = response.data;
      set(state => ({
        stories: state.stories.map(s => s.id === storyId ? updated : s),
        currentStory: state.currentStory?.id === storyId ? updated : state.currentStory,
        isLoading: false,
      }));
      return updated;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось обновить историю', isLoading: false });
      throw error;
    }
  },

  deleteStory: async (storyId) => {
    set({ isLoading: true, error: null });
    try {
      await storiesAPI.delete(storyId);
      set(state => ({
        stories: state.stories.filter(s => s.id !== storyId),
        currentStory: state.currentStory?.id === storyId ? null : state.currentStory,
        isLoading: false,
      }));
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось удалить историю', isLoading: false });
      throw error;
    }
  },

  // ==================== Members ====================

  fetchMembers: async (familyId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await familiesAPI.listMembers(familyId);
      set({ members: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось загрузить участников', isLoading: false });
      throw error;
    }
  },

  updateMemberRole: async (familyId, memberId, role) => {
    set({ isLoading: true, error: null });
    try {
      const response = await familiesAPI.updateMember(familyId, memberId, { role });
      set(state => ({
        members: state.members.map(m => m.id === memberId ? response.data : m),
        isLoading: false,
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось обновить участника', isLoading: false });
      throw error;
    }
  },

  removeMember: async (familyId, memberId) => {
    set({ isLoading: true, error: null });
    try {
      await familiesAPI.removeMember(familyId, memberId);
      set(state => ({
        members: state.members.filter(m => m.id !== memberId),
        isLoading: false,
      }));
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось удалить участника', isLoading: false });
      throw error;
    }
  },

  // ==================== Invitations ====================

  fetchInvitations: async (familyId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await familiesAPI.listInvitations(familyId);
      set({ invitations: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось загрузить приглашения', isLoading: false });
      throw error;
    }
  },

  createInvitation: async (familyId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await familiesAPI.createInvitation(familyId, data);
      set(state => ({
        invitations: [...state.invitations, response.data],
        isLoading: false,
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось создать приглашение', isLoading: false });
      throw error;
    }
  },

  revokeInvitation: async (familyId, invitationId) => {
    set({ isLoading: true, error: null });
    try {
      await familiesAPI.revokeInvitation(familyId, invitationId);
      set(state => ({
        invitations: state.invitations.filter(i => i.id !== invitationId),
        isLoading: false,
      }));
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Не удалось отозвать приглашение', isLoading: false });
      throw error;
    }
  },

  // ==================== Helpers ====================

  clearError: () => set({ error: null }),

  clearCurrentFamily: () => set({
    currentFamily: null,
    currentFamilyId: null,
    treeData: null,
    persons: [],
    stories: [],
    members: [],
    invitations: [],
  }),

  clearCurrentPerson: () => set({ currentPerson: null }),

  clearCurrentStory: () => set({ currentStory: null }),

  reset: () => set({
    families: [],
    currentFamily: null,
    currentFamilyId: null,
    persons: [],
    currentPerson: null,
    treeData: null,
    stories: [],
    currentStory: null,
    members: [],
    invitations: [],
    isLoading: false,
    error: null,
  }),
    }),
    {
      name: 'letopis-family',
      partialize: (state) => ({
        currentFamilyId: state.currentFamilyId,
      }),
    }
  )
);

export default useFamilyStore;

